import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnInit, SimpleChange, ViewChild } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Util } from '@libs/utilities/util';

import { NgAbstractInputComponent } from '../../shared/inputs.abstract';
import { INgCustomDropdownOption } from './custom-dropdown.interface';


@Component({
    selector: 'ng-custom-dropdown',
    templateUrl: './custom-dropdown.component.html',
    styleUrls: ['./custom-dropdown.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: NgCustomDropdownComponent,
            multi: true
        }
    ],
    standalone: false
})
export class NgCustomDropdownComponent<T = string> extends NgAbstractInputComponent<T> implements OnInit, OnChanges, ControlValueAccessor {

	@ViewChild('dropdownRef', { static: true }) dropdownRef: NgbDropdown;
	@ViewChild('dropdownMenu', { static: true }) dropdownMenu: ElementRef<HTMLElement>;

	//** Dropdown menu options */
	@Input() options 		: INgCustomDropdownOption<T>[];
	@Input() onlyText 		: boolean = false;
	@Input() customTemplate : boolean = false;
	@Input() closeOnScroll 	: boolean = true;
	@Input() closeOnSelect 	: boolean = true;
	@Input() elementId 		: string = '';
	@Input() dropdownClass 	: string = '';
	@Input() toggleClass 	: string = '';
	@Input() matchToggleWidth: boolean = true;
	@Input() container		: 'body' | null = 'body';
	@Input() display			: 'dynamic' | 'static' = 'dynamic';

	//** Helper Variables */
	selectedOption: INgCustomDropdownOption<T> = null!;

	constructor() {
		super('custom-dropdown');
	}

	ngOnInit(): void {

		//0 - validate options
		this.validateOptions();

		//1 - if the ng-content is active then disable onlyText template
		if (this.customTemplate) this.onlyText = false;
	}

	ngOnChanges(changes: { options: SimpleChange }): void {
		if (changes.options) this.validateOptions();
	}


	/**------------------------------------------------------
	 * Control Value Accessor Functions
	 */
	override writeValue(data: T): void {

		//0 - update value
		this.value = data;

		//1 - selected option check
		if (Util.Basic.isUndefined(data)) {
			this.selectedOption = null!;
			return;
		}
		const optionData: INgCustomDropdownOption<T> | undefined = this.options.find((menuOption: INgCustomDropdownOption<T>) => menuOption.value === this.value);
		if (!optionData) throw new Error(`NgCustomDropdownComponent => writeValue => FATAL ERROR: Selected Option does not exists in the dropdown menu options: ${data}`);

		//2 - update the selected option to show in the view
		this.selectedOption = optionData;
	}


	/**------------------------------------------------------
	 * Ui Helper Functions
	 */
	selectOption(option: INgCustomDropdownOption<T>): void {

		//0 - check if the option is already selected
		if (this.closeOnSelect) this.dropdownRef.close();

		//1 - update the selected option
		this.selectedOption = option;
		this.value = option.value;

		//2 - emit the new value
		this.onChange(option.value);
		this.onTouched();
	}

	trackOptionByValue(index: number, option: INgCustomDropdownOption<T>): T | number {
		return option.value ?? index;
	}

	onDropdownOpenChange(isOpen: boolean): void {
		if (!isOpen) return;

		setTimeout(() => {
			const menuElement: HTMLElement = this.dropdownMenu.nativeElement;
			const selectedElement: HTMLElement | null = menuElement.querySelector('.ng-custom-dropdown-option.active');
			if (!selectedElement) return;

			const selectedTop: number = selectedElement.offsetTop;
			const selectedBottom: number = selectedTop + selectedElement.offsetHeight;
			const visibleTop: number = menuElement.scrollTop;
			const visibleBottom: number = visibleTop + menuElement.clientHeight;
			if (selectedTop >= visibleTop && selectedBottom <= visibleBottom) return;

			menuElement.scrollTop = selectedTop - ((menuElement.clientHeight - selectedElement.offsetHeight) / 2);
		}, 0);
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	private validateOptions(): boolean {

		//0 - check if the options is empty
		if (Util.Array.isEmpty(this.options)) throw new Error(`NgCustomDropdownComponent => ngOnInit => FATAL ERROR: options is empty: ${this.options}`);

		//1 - check if the options has both icon and image
		const hasIconAndImage: boolean = this.options.some((option: INgCustomDropdownOption<T>) => Util.Basic.isDefined(option.icon) && Util.Basic.isDefined(option.img));
		if (!hasIconAndImage) return true;

		//2 - return valid
		this.options = [];
		throw new Error(`NgCustomDropdownComponent => ngOnInit => FATAL ERROR: options has both icon and image, only one is allowed`);
	}
}
